import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Package,
  Truck,
  CheckCircle2,
  Clock,
  Phone,
  MapPin,
  XCircle,
  Navigation,
  Loader2,
  RefreshCw,
  ArrowLeft,
  ClipboardList,
  ClipboardCheck,
  Boxes,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { formatDateTime } from '../lib/dateFormat';
import { resolveImageUrl } from '../components/ImageUpload';
import Rial from '../components/Rial';
import { orderPartyPrimary, orderPartyContact } from '../lib/orderParty';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const driverIcon = L.divIcon({
  className: '',
  iconSize: [36, 44],
  iconAnchor: [18, 42],
  html: `
    <div style="position:relative;width:36px;height:44px;">
      <svg viewBox="0 0 36 44" width="36" height="44">
        <path d="M18 0 C8 0 0 8 0 18 c0 12 18 26 18 26 s18-14 18-26 c0-10-8-18-18-18z"
          fill="#10b981" stroke="#fff" stroke-width="2"/>
      </svg>
      <div style="position:absolute;top:6px;left:0;right:0;text-align:center;color:#fff;font-size:16px;">🚚</div>
    </div>`,
});

const destIcon = L.divIcon({
  className: '',
  iconSize: [30, 38],
  iconAnchor: [15, 36],
  html: `
    <div style="position:relative;width:30px;height:38px;">
      <svg viewBox="0 0 30 38" width="30" height="38">
        <path d="M15 0 C7 0 0 7 0 15 c0 10 15 23 15 23 s15-13 15-23 c0-8-7-15-15-15z"
          fill="#ef4444" stroke="#fff" stroke-width="2"/>
      </svg>
      <div style="position:absolute;top:4px;left:0;right:0;text-align:center;color:#fff;font-size:14px;">📍</div>
    </div>`,
});

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 14);
    } else {
      map.fitBounds(points, { padding: [60, 60], maxZoom: 15 });
    }
  }, [points, map]);
  return null;
};

const STEPS = [
  { key: 'created_at', status: 'confirmed', label: 'Order placed', icon: Package },
  // Feb 2026 — warehouse pick/load stages sit between order confirmation
  // and dispatch. Pick list is generated automatically when the order is
  // placed; picking starts when the warehouse team opens the task;
  // loading is a manual step from the warehouse manager screen.
  { key: 'pick_list_generated_at', status: 'pick_list_generated', label: 'Pick list generated', icon: ClipboardList },
  { key: 'picking_started_at', status: 'picking_in_progress', label: 'Picking in progress', icon: ClipboardCheck },
  { key: 'loading_started_at', status: 'loading_in_progress', label: 'Loading in progress', icon: Boxes },
  { key: 'driver_assigned_at', status: 'assigned', label: 'Driver assigned', icon: Truck },
  { key: 'picked_up_at', status: 'picked_up', label: 'Picked up', icon: Package },
  { key: 'out_for_delivery_at', status: 'out_for_delivery', label: 'Out for delivery', icon: Navigation },
  { key: 'delivered_at', status: 'delivered', label: 'Delivered', icon: CheckCircle2 },
];

const STATUS_TONE = {
  new: 'bg-blue-100 text-blue-700',
  confirmed: 'bg-blue-100 text-blue-700',
  assigned: 'bg-blue-100 text-blue-700',
  picked_up: 'bg-amber-100 text-amber-700',
  out_for_delivery: 'bg-purple-100 text-purple-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-700',
};

const TrackOrder = () => {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      // Feb 2026 — bust every cache along the wire (browser, ingress,
      // Cloudflare) so admin status updates surface on the public
      // tracker within a single 5s poll. Cache-Control on the request
      // + a rotating `_t` query bust the two layers we've observed
      // holding on to the previous `status`.
      const { data } = await axios.get(
        `${API}/track/${orderNumber}?_t=${Date.now()}`,
        { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } },
      );
      setOrder(data);
      setError(null);
    } catch (err) {
      setError(err?.response?.status === 404 ? 'Order not found' : 'Could not load tracking info');
    } finally {
      setLoading(false);
    }
  }, [orderNumber]);

  useEffect(() => {
    load();
    // Public tracker auto-refreshes every 5 seconds so the customer sees driver
    // location and status updates in near real-time without having to reload.
    // Pause when the tab is hidden — saves the customer's battery + the
    // tracking endpoint's load, and resumes immediately on focus.
    let t = null;
    const start = () => {
      if (t) return;
      t = setInterval(load, 5000);
    };
    const stop = () => {
      if (t) { clearInterval(t); t = null; }
    };
    if (!document.hidden) start();
    const onVis = () => {
      if (document.hidden) {
        stop();
      } else {
        load();
        start();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [load]);

  const reachedIndex = (() => {
    if (!order) return -1;
    if (order.status === 'failed') return -2;
    let idx = 0;
    STEPS.forEach((s, i) => {
      if (order[s.key]) idx = i;
    });
    return idx;
  })();

  const driverPos = order?.driver?.lat && order?.driver?.lng ? [order.driver.lat, order.driver.lng] : null;
  const destPos = order?.location_lat && order?.location_lng ? [order.location_lat, order.location_lng] : null;
  const points = [driverPos, destPos].filter(Boolean);

  return (
    <main className="min-h-screen bg-[#F8FAFC] pb-20">
      {/* Header */}
      <header className="bg-[#0F172A] text-white">
        <div className="max-w-4xl mx-auto px-5 py-6 flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <Link to="/" className="text-[10px] font-bold tracking-[0.25em] text-[#71B7F0] uppercase hover:underline">
              Mohammed Riaz & Partner LLC
            </Link>
            <h1 className="layout-page-title font-display text-2xl sm:text-3xl mt-0.5">Track your order</h1>
            <div className="text-xs text-slate-400 mt-1">Order #{orderNumber}</div>
            {order?.erp_invoice_number && (
              <div
                className="text-[11px] text-emerald-700 font-semibold mt-1 font-mono"
                data-testid="track-erp-invoice"
              >
                ERP Invoice #{order.erp_invoice_number}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* "Back to My Orders" — visible only when the visitor has a
                live branch session OR the order itself is a branch order.
                Storefront customers never see it (they land on /track from
                the confirmation email and have no such page to return to). */}
            {(typeof window !== 'undefined' && window.localStorage?.getItem('branch_token')) || order?.order_type === 'branch' ? (
              <Link
                to="/branch/orders"
                data-testid="track-back-to-my-orders"
                className="inline-flex items-center gap-1.5 rounded-full bg-white text-[#0F172A] hover:bg-white/90 px-4 h-10 text-sm font-semibold transition"
              >
                <ArrowLeft className="w-4 h-4" /> Back to My Orders
              </Link>
            ) : null}
            <Button
              onClick={load}
              variant="ghost"
              className="text-white hover:bg-white/10 rounded-full"
              data-testid="track-refresh"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-5 -mt-4">
        {loading && !order ? (
          <div className="bg-white rounded-2xl border border-neutral-200 p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#0078D4] mx-auto mb-3" />
            <p className="text-neutral-500">Loading…</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl border border-neutral-200 p-12 text-center" data-testid="track-error">
            <XCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <h2 className="font-display text-xl font-bold text-[#0F172A] mb-1">{error}</h2>
            <p className="text-neutral-500 text-sm">
              Double-check your order number and try again, or contact support.
            </p>
            <Link to="/" className="text-[#0078D4] text-sm font-semibold hover:underline mt-3 inline-block">
              ← Back to store
            </Link>
          </div>
        ) : (
          <>
            {/* Summary card */}
            <div className="bg-white rounded-2xl border border-neutral-200 p-6 mb-4 shadow-sm" data-testid="track-summary">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                <div>
                  <div className="text-[10px] font-bold tracking-widest uppercase text-neutral-500">Status</div>
                  <div className="font-display text-2xl font-bold text-[#0F172A] mt-0.5">
                    {order.status === 'failed' ? 'Delivery failed' :
                     order.status === 'delivered' ? 'Delivered ✓' :
                     order.status === 'out_for_delivery' ? 'On its way' :
                     order.status === 'picked_up' ? 'Picked up' :
                     order.status === 'assigned' ? 'Driver assigned' :
                     'Confirmed'}
                  </div>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${STATUS_TONE[order.status] || 'bg-neutral-200 text-neutral-700'}`}
                  data-testid="track-status-pill"
                >
                  {order.status.replace(/_/g, ' ')}
                </span>
              </div>

              {order.status === 'failed' && order.failure_reason && (
                <div className="rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm p-3 mb-3">
                  Reason: {order.failure_reason}
                </div>
              )}

              {/* Timeline */}
              <div className="relative pl-3 mt-2" data-testid="track-timeline">
                {STEPS.map((s, i) => {
                  const reached = reachedIndex >= 0 && i <= reachedIndex;
                  const isCurrent = i === reachedIndex && order.status !== 'delivered';
                  const ts = order[s.key];
                  return (
                    <div key={s.key} className="flex gap-3 pb-4 last:pb-0 relative">
                      {i < STEPS.length - 1 && (
                        <span
                          className={`absolute left-[10px] top-6 bottom-0 w-0.5 ${
                            reached && i < reachedIndex ? 'bg-emerald-500' : 'bg-neutral-200'
                          }`}
                        />
                      )}
                      <div
                        className={`relative w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center mt-1 ${
                          reached ? 'bg-emerald-500' : 'bg-neutral-200'
                        } ${isCurrent ? 'ring-4 ring-emerald-200 animate-pulse' : ''}`}
                      >
                        {reached && <CheckCircle2 className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1">
                        <div className={`text-sm font-semibold ${reached ? 'text-[#0F172A]' : 'text-neutral-400'}`}>
                          {s.label}
                        </div>
                        {ts && (
                          <div className="text-xs text-neutral-500 mt-0.5">{formatDateTime(ts)}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Driver + map */}
            {order.driver && (
              <div className="bg-white rounded-2xl border border-neutral-200 p-5 mb-4 flex items-center gap-4" data-testid="track-driver">
                {order.driver.photo ? (
                  <img
                    src={resolveImageUrl(order.driver.photo)}
                    alt={order.driver.name}
                    className={`w-16 h-16 rounded-full object-cover ring-2 ${
                      order.status === 'out_for_delivery'
                        ? 'ring-emerald-400 ring-offset-2 animate-pulse'
                        : 'ring-emerald-100'
                    }`}
                    data-testid="track-driver-photo"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-2xl">
                    {order.driver.name?.charAt(0).toUpperCase() || 'D'}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold tracking-widest uppercase text-neutral-500">Your driver</div>
                  <div className="font-display text-lg font-bold text-[#0F172A] truncate">{order.driver.name}</div>
                  {order.driver.vehicle && (
                    <div className="text-xs text-neutral-500 truncate">{order.driver.vehicle}</div>
                  )}
                </div>
                {order.driver.phone && (
                  <a
                    href={`tel:${order.driver.phone}`}
                    className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 text-sm font-bold flex items-center gap-1.5 transition flex-shrink-0"
                    data-testid="call-driver-btn"
                  >
                    <Phone className="w-4 h-4" /> Call
                  </a>
                )}
              </div>
            )}

            {points.length > 0 && (
              <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden h-[400px]" data-testid="track-map">
                <MapContainer center={points[0]} zoom={13} scrollWheelZoom={false} className="h-full w-full">
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <FitBounds points={points} />
                  {driverPos && <Marker position={driverPos} icon={driverIcon} />}
                  {destPos && <Marker position={destPos} icon={destIcon} />}
                </MapContainer>
              </div>
            )}

            {/* Order details */}
            <div className="bg-white rounded-2xl border border-neutral-200 p-5 mt-4" data-testid="track-meta">
              <div className={`grid ${order.order_type === 'branch' ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} gap-4 text-sm`}>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-1">Customer</div>
                  <div className="text-[#0F172A]" data-testid="track-order-party">{orderPartyPrimary(order)}</div>
                  {orderPartyContact(order) && (
                    <div className="text-xs text-neutral-500 mt-0.5" data-testid="track-order-contact">Contact: {orderPartyContact(order)}</div>
                  )}
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-1">Items</div>
                  <div className="text-[#0F172A]">{order.items_count} item(s)</div>
                </div>
                {order.order_type !== 'branch' && (
                  <div data-testid="track-order-total">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-1">Total</div>
                    <div className="text-[#0F172A] font-bold"><Rial /> {Number(order.total || 0).toFixed(3)}</div>
                  </div>
                )}
              </div>
              {order.location_address && (
                <div className="mt-3 flex items-start gap-1.5 text-sm text-neutral-700">
                  <MapPin className="w-4 h-4 mt-0.5 text-[#0078D4] flex-shrink-0" />
                  {order.location_address}
                </div>
              )}
            </div>

            {/* Secondary "Back to My Orders" at the foot of the tracking
                view — same visibility rule as the header button so long
                tracking pages don't force a scroll-back-to-top. */}
            {(typeof window !== 'undefined' && window.localStorage?.getItem('branch_token')) || order?.order_type === 'branch' ? (
              <div className="mt-6 flex justify-center">
                <Link
                  to="/branch/orders"
                  data-testid="track-back-to-my-orders-bottom"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#0F172A] hover:bg-black text-white px-5 h-11 text-sm font-semibold transition shadow-sm"
                >
                  <ArrowLeft className="w-4 h-4" /> Back to My Orders
                </Link>
              </div>
            ) : null}
          </>
        )}
      </section>
    </main>
  );
};

export default TrackOrder;
